import t from '@jetshop/intl';
import React, { useContext } from 'react';
import { styled } from 'linaria/react';

import { cx, css } from 'linaria';

import { StoreMarker } from './StoreMarker';

import { ReactComponent as CarrotSvg } from '@jetshop/ui/svg/Carrot.svg';
import { ReactComponent as DirectionsSvg } from '../../svg/Directions.svg';
import { LocationStateContext } from './StoreLocator';
import UserLocation from './UserLocation';

import { theme } from '../Theme';

const StoreListContainer = styled('ul')`
  /* height: calc(100% - 94px); */
  overflow-y: auto;
  margin: 0 20px;
  list-style-type: none;
  padding: 0;

  .highlight {
    background: yellow;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  ${theme.below.lg} {
    margin: 40px 0 0 0;
  }
`;

const StoreContainer = styled('li')`
  position: relative;
  width: 100%;
  background: #f7f7f7;
  border: 1px solid #d3d3d3;
  border-radius: 5px;
  padding: 16px;
  margin: 8px 0;

  font-size: 14px;
  line-height: 1.4;

  cursor: pointer;

  &:first-child {
    margin-top: 0;
  }
  &:last-child {
    margin-bottom: 0;
  }

  > div {
    display: flex;

    &.expanded-content {
      max-height: 0;
      overflow: hidden;
      transition-property: max-height;
      transition-duration: 0.2s;

      .expanded-inner {
        display: flex;
        flex-direction: column;
        width: 100%;

        .inner-column {
          margin-left: 0;
        }

        > div {
          padding: 20px 0;
        }
      }
    }
  }

  &.expanded {
    > div.expanded-content {
      max-height: 600px;
    }

    svg.carrot {
      transform: rotate(0deg);
    }
  }

  svg.carrot {
    transition-property: transform;
    transition-duration: 0.2s;
  }
`;

const StoreColumn = styled('div')`
  text-align: left;
  margin-left: 10px;
  position: relative;

  .storename {
    text-transform: uppercase;
  }

  .adress {
    font-size: 12px;
  }

  .contact-icon {
    position: absolute;
    left: 0;
  }

  a {
    color: #000;
    padding: 4px 0 8px;
  }
`;

const secondStoreColumn = css`
  padding: 0 8px 0 16px;
`;

const CarrotContainer = styled('div')`
  position: absolute;
  right: 24px;
  flex: 0 0 24px;
  /* background: ${theme.colors.layout.secondaryBackground}; */
  width: 24px;
  height: 24px;
  border-radius: 50%;
  align-self: center;

  svg {
    width: 12px;
    height: 12px;
    margin: 6px;

    transform: rotate(-90deg);
  }
`;

const CoordinatesCTA = styled('div')`
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.accent};
  color: ${theme.colors.accentTextColor};
  border-radius: ${theme.button.desktop.borderRadius};

  a {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
    text-decoration: none;
  }
`;

const humanDistance = distance =>
  distance > 10
    ? t('{km} km', { km: Math.round(distance) })
    : distance > 1
    ? t('{km} km', { km: distance.toFixed(1) })
    : t('{m} m', { m: Math.round(distance * 1000) });

const contactParser = html =>
  html
    ?.replace('Tel: ', '<span class="contact-icon">📞</span>')
    ?.replace('Email: ', '<span class="contact-icon">🖂</span>');

const Carrot = () => (
  <CarrotContainer>
    <CarrotSvg className="carrot" />
  </CarrotContainer>
);

const Store = ({
  storeNr,
  distances,
  setClickedStore,
  store: { id, name, address1, openHours, description, coordinates, contact }
}) => {
  const locationState = useContext(LocationStateContext);

  const active = id === locationState.activeLocation;
  const expanded = id === locationState.expandedLocation;

  return (
    <StoreContainer
      onMouseEnter={() => locationState.setHighlightedLocation(id)}
      onMouseLeave={() => locationState.setHighlightedLocation(null)}
      onClick={() => {
        setClickedStore(id);
        locationState.toggleExpandedLocation(id);
      }}
      onKeyPress={e => {
        if ((e.keyCode || e.which) === 13)
          locationState.toggleExpandedLocation(id);
      }}
      tabIndex={0}
      className={cx(expanded ? 'expanded' : null)}
    >
      <div>
        <StoreMarker nr={storeNr} active={active} />
        <StoreColumn>
          <p dangerouslySetInnerHTML={{ __html: name }} className="storename" />

          <div
            dangerouslySetInnerHTML={{ __html: address1 }}
            className="adress"
          />
        </StoreColumn>
        <Carrot />
      </div>
      <div className="expanded-content">
        <div className="expanded-inner">
          <div>
            <StoreColumn
              dangerouslySetInnerHTML={{ __html: description }}
              className="inner-column"
            />
            {contact && (
              <StoreColumn className="inner-column">
                <p>
                  <strong>{t('contact')}</strong>
                </p>
                <div
                  dangerouslySetInnerHTML={{ __html: contactParser(contact) }}
                  style={{ marginBottom: '24px' }}
                />
              </StoreColumn>
            )}
            {/* <StoreColumn className="inner-column">
              <p>
                <strong>{t('Opening hours')}</strong>
              </p>
              <div dangerouslySetInnerHTML={{ __html: openHours }} />
              {!expanded && distances[id] && (
                <p style={{ color: '#8a8a8a' }}>
                  {t('Distance')}: {humanDistance(distances[id])}
                </p>
              )}
            </StoreColumn> */}
          </div>
          <div style={{ padding: '0' }}>
            {coordinates && (
              <CoordinatesCTA>
                <p>
                  {/* <DirectionsSvg className="contact-icon" /> */}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.google.com/maps/dir/${coordinates.latitude},${coordinates.longitude}`}
                  >
                    {t('Directions')}
                  </a>
                </p>
                {distances[id] && <p>{humanDistance(distances[id])}</p>}
              </CoordinatesCTA>
            )}
          </div>
        </div>
      </div>
    </StoreContainer>
  );
};

const StoreList = ({ stores, distances, setClickedStore }) => (
  <>
    <StoreListContainer>
      {stores &&
        stores?.map((store, index) => (
          <Store
            key={index}
            storeNr={index + 1}
            store={store}
            distances={distances}
            setClickedStore={setClickedStore}
          />
        ))}
    </StoreListContainer>
  </>
);

export default StoreList;
